import React from 'react';
import { ArrowRight, CheckCircle, Circle, CurrencyEur, Buildings } from '@phosphor-icons/react';
import { countryName } from '../../lib/countries';

export const money = (v, currency = 'EUR') => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (Number.isNaN(n)) return null;
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
};

const dateLabel = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

export const SectionHeading = ({ children, hint }) => (
  <div className="mb-5">
    <h3 className="text-[11px] uppercase tracking-[0.18em] text-foreground-muted font-medium">{children}</h3>
    {hint && <p className="text-sm text-foreground-muted mt-2">{hint}</p>}
  </div>
);

export const TheOpportunity = ({ project }) => {
  const [expanded, setExpanded] = React.useState(false);
  const text = (project?.description || '').trim();
  if (!text) return null;
  const isLong = text.length > 420;
  return (
    <section data-testid="the-opportunity">
      <SectionHeading>The opportunity</SectionHeading>
      {project?.tagline && (
        <p className="text-xl md:text-2xl font-heading font-semibold leading-snug tracking-tight mb-4">
          {project.tagline}
        </p>
      )}
      <p className="text-sm text-foreground-muted leading-relaxed whitespace-pre-line">
        {isLong && !expanded ? `${text.slice(0, 420).trimEnd()}…` : text}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary"
          data-testid="opportunity-read-more"
        >
          {expanded ? 'Show less' : 'Read more'} <ArrowRight size={15} weight="bold" />
        </button>
      )}
    </section>
  );
};

export const ProjectPlan = ({ plan, nextMilestone }) => {
  if (!plan?.stages?.length) return null;
  return (
    <section data-testid="project-plan">
      <SectionHeading>Project plan</SectionHeading>
      <div className="flex flex-col md:flex-row md:items-start md:gap-4">
        {plan.stages.map((stage, i) => (
          <div key={stage.key} className="flex md:flex-col md:flex-1 items-start gap-3 md:gap-2 pb-4 md:pb-0 relative">
            <div className="flex md:w-full items-center gap-3 md:gap-2">
              {stage.state === 'complete' ? (
                <CheckCircle size={20} weight="fill" className="text-[rgb(34,150,94)] flex-shrink-0" />
              ) : stage.state === 'current' ? (
                <span className="h-[18px] w-[18px] rounded-full bg-primary flex-shrink-0 ring-4 ring-primary/15" />
              ) : (
                <Circle size={18} className="text-black/20 flex-shrink-0" />
              )}
              <span className="hidden md:block h-[2px] flex-1 bg-black/[0.07]" />
            </div>
            <div className="md:mt-2" data-testid={`plan-stage-${i}`}>
              <p className={`text-sm ${stage.state === 'upcoming' ? 'text-foreground-muted' : 'font-medium'}`}>{stage.label}</p>
              {stage.state === 'current' && <p className="text-xs text-primary mt-0.5">Current stage</p>}
              {stage.date && <p className="text-xs text-foreground-muted mt-0.5">{dateLabel(stage.date)}</p>}
            </div>
          </div>
        ))}
      </div>
      {nextMilestone && (
        <div className="mt-6 p-5 rounded-2xl bg-[rgb(var(--surface-muted,250_250_250))] border border-black/[0.05]" data-testid="next-milestone">
          <p className="text-[11px] uppercase tracking-[0.16em] text-foreground-muted">Next milestone</p>
          <p className="text-sm font-medium mt-1.5">{nextMilestone.title}</p>
          {nextMilestone.expected_label && (
            <p className="text-sm text-foreground-muted mt-0.5">Expected {nextMilestone.expected_label}</p>
          )}
        </div>
      )}
    </section>
  );
};

export const PropertyDetails = ({ asset, project }) => {
  const area = (v) => (v ? `${v} m²` : null);
  const clean = (group) =>
    group
      .map(([label, value]) => [label, typeof value === 'string' ? value.replace(/_/g, ' ') : value])
      .filter(([, v]) => v !== null && v !== undefined && v !== '');

  const groups = [
    ['Layout', clean([
      ['Property type', asset?.property_form || asset?.asset_type],
      ['Use', asset?.property_use],
      ['Bedrooms', asset?.bedrooms],
      ['Bathrooms', asset?.bathrooms],
      ['Parking spaces', asset?.parking_spaces],
      ['Floor', asset?.floor_number],
      ['Floors in building', asset?.floors],
      ['Units', asset?.units_count],
    ])],
    ['Areas', clean([
      ['Usable area', area(asset?.usable_area_sqm)],
      ['Gross area', area(asset?.gross_area_sqm)],
      ['Total area', area(asset?.total_area_sqm)],
      ['Outdoor area', area(asset?.external_area_sqm)],
      ['Plot area', area(asset?.land_area_sqm)],
    ])],
    ['Condition', clean([
      ['Condition', asset?.condition],
      ['Occupancy', asset?.occupancy],
      ['Year built', asset?.year_built],
      ['Renovated', asset?.year_renovated],
      ['Energy rating', asset?.energy_rating],
      ['Zoning', asset?.zoning],
      ['Strategy', project?.project_type_label],
    ])],
    ['Location', clean([
      ['Address', asset?.address_line_1],
      ['Neighbourhood', asset?.neighborhood],
      ['City', asset?.city || project?.city],
      ['Region', asset?.region],
      ['Postal code', asset?.postal_code],
      ['Country', countryName(asset?.country_code || project?.country_code)],
    ])],
  ].filter(([, rows]) => rows.length);

  if (!groups.length) return null;
  const chips = [...(asset?.features || []), ...(asset?.amenities || [])];

  return (
    <section data-testid="property-details">
      <SectionHeading>Property details</SectionHeading>
      <div className="divide-y divide-black/[0.06]">
        {groups.map(([group, rows]) => (
          <div key={group} className="py-6 first:pt-0 last:pb-0 md:grid md:grid-cols-4 md:gap-8">
            <p className="text-[11px] uppercase tracking-[0.16em] text-primary font-medium mb-4 md:mb-0">{group}</p>
            <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-y-5 gap-x-8">
              {rows.map(([label, value]) => (
                <div key={label}>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted">{label}</p>
                  <p className={`text-sm font-medium mt-1 ${typeof value === 'string' && !/m²/.test(value) ? 'capitalize' : ''}`}>
                    {value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-7">
          {chips.map((f) => (
            <span key={f} className="px-3 py-1.5 rounded-full text-xs bg-black/[0.04] text-foreground-muted capitalize">
              {String(f).replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
    </section>
  );
};

export const InvestmentThesis = ({ project, drivers }) => {
  const thesis = (project?.investment_thesis || '').trim();
  const description = (project?.description || '').trim();
  const norm = (t) => t.toLowerCase().replace(/\s+/g, ' ').slice(0, 120);
  const sameAsDescription = Boolean(thesis && description && norm(thesis) === norm(description));
  if (!drivers?.length && (!thesis || sameAsDescription)) return null;
  return (
    <section data-testid="investment-thesis">
      <SectionHeading>Why this investment</SectionHeading>
      {thesis && !sameAsDescription && (
        <p className="text-sm text-foreground-muted leading-relaxed mb-6 whitespace-pre-line">{thesis}</p>
      )}
      {drivers?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
          {drivers.map((d) => (
            <div key={d.title} data-testid={`thesis-driver-${d.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="text-[11px] uppercase tracking-[0.16em] text-primary font-medium">{d.title}</p>
              <p className="text-sm text-foreground-muted leading-relaxed mt-2">{d.body}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export const PropertyEconomics = ({ economics, onViewFinancials, currency = 'EUR' }) => {
  const rows = (economics || []).filter((e) => e.value !== null && e.value !== undefined);
  if (!rows.length) return null;
  return (
    <section data-testid="property-economics">
      <SectionHeading>Property economics</SectionHeading>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-8">
        {rows.map((row) => (
          <div key={row.label}>
            <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted">{row.label}</p>
            <p className="text-sm font-medium mt-1">
              {row.kind === 'money' ? money(row.value, currency) : row.value}
            </p>
            {row.note && <p className="text-xs text-foreground-muted mt-0.5">{row.note}</p>}
          </div>
        ))}
      </div>
      <button
        onClick={onViewFinancials}
        className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
        data-testid="view-full-financials"
      >
        <CurrencyEur size={16} weight="bold" /> View full financials
      </button>
    </section>
  );
};

export const LocationSection = ({ asset, project }) => {
  const city = asset?.city || project?.city;
  const country = countryName(asset?.country_code || project?.country_code);
  const coords = asset?.coordinates;
  const query = coords?.lat && coords?.lng
    ? `${coords.lat},${coords.lng}`
    : [asset?.address_line_1, city, country].filter(Boolean).join(', ');
  if (!query) return null;
  const bbox = coords?.lat && coords?.lng
    ? `${coords.lng - 0.02},${coords.lat - 0.015},${coords.lng + 0.02},${coords.lat + 0.015}`
    : null;
  const src = bbox
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${coords.lat},${coords.lng}`
    : `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=12&output=embed`;
  return (
    <section data-testid="location-section">
      <SectionHeading>Location</SectionHeading>
      <p className="text-sm font-medium mb-4 flex items-center gap-2">
        <Buildings size={18} weight="duotone" className="text-primary" />
        {[asset?.neighborhood, city, country].filter(Boolean).join(', ')}
      </p>
      <div className="rounded-2xl overflow-hidden border border-black/[0.06] h-[260px] md:h-[340px]">
        <iframe
          title="Property location"
          src={src}
          className="w-full h-full"
          loading="lazy"
          style={{ border: 0 }}
        />
      </div>
    </section>
  );
};

export const UnderlyingProperties = ({ assets, project, onOpen }) => {
  if (!assets?.length) return null;
  return (
    <section data-testid="underlying-properties">
      <SectionHeading hint={`${assets.length} properties`}>Underlying properties</SectionHeading>
      <div className="flex gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-2 lg:grid-cols-3 md:overflow-visible">
        {assets.map((asset) => (
          <button
            key={asset.id}
            onClick={() => onOpen(asset)}
            className="text-left flex-shrink-0 w-[260px] md:w-auto group"
            data-testid={`property-card-${asset.code}`}
          >
            <div className="h-[170px] rounded-2xl overflow-hidden bg-black/[0.04]">
              {asset.image_urls?.[0] && (
                <img src={asset.image_urls[0]} alt={asset.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              )}
            </div>
            <p className="text-sm font-medium mt-3">{asset.name}</p>
            <p className="text-sm text-foreground-muted capitalize">
              {[asset.bedrooms ? `${asset.bedrooms}-bedroom` : null, (asset.asset_type || '').replace(/_/g, ' ')].filter(Boolean).join(' ')}
            </p>
            <p className="text-sm text-foreground-muted">
              {[asset.city || project?.city, countryName(asset.country_code || project?.country_code)].filter(Boolean).join(', ')}
            </p>
            {asset.current_valuation && (
              <p className="text-sm font-medium mt-1">{money(asset.current_valuation)} current value</p>
            )}
            <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
              View property <ArrowRight size={14} weight="bold" />
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};
